import React, { useState } from "react";
import { philosophyApi } from "../api/routes";
import { Button, Card, Input, Select, TextArea, Toast, TwoColumn } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const distanceOptions = [
  { label: "5K", value: "FIVE_K" },
  { label: "10K", value: "TEN_K" },
  { label: "Half Marathon", value: "HALF_MARATHON" },
  { label: "Marathon", value: "MARATHON" },
  { label: "Ultra", value: "ULTRA_MARATHON" },
  { label: "Off Season", value: "OFF_SEASON" },
];

const blank = {
  philosophyId: "",
  philosophyVersion: "",
  lastUpdated: "",
  distanceType: "",
  description: "",
  name: "",
  philosophies: [],
  baseWorkoutPlanId: "",
  favWorkouts: [],
};

export default function PhilosophiesPanel() {
  const [form, setForm] = useState(blank);
  const [pillarsInput, setPillarsInput] = useState("");
  const [favWorkoutsInput, setFavWorkoutsInput] = useState("");
  const [distance, setDistance] = useState("");
  const [philosophyLookup, setPhilosophyLookup] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("search"); // search | edit
  const [toast, setToast] = useState(null);
  const [panelState, setPanelState] = usePanelCache("philosophies");
  const list = panelState.list || [];
  const showSearch = panelState.showSearch ?? true;
  const [searching, setSearching] = useState(false);

  const parseListInput = (value) =>
    value
      .split(/[\n,]+/)
      .map((v) => v.trim())
      .filter(Boolean);

  const loadForm = (data = blank) => {
    const merged = { ...blank, ...data };
    setForm(merged);
    setPillarsInput((merged.philosophies || []).join("\n"));
    setFavWorkoutsInput((merged.favWorkouts || []).join("\n"));
  };

  const fetchByDistance = async () => {
    setSearching(true);
    try {
      const res = await philosophyApi.byDistance(distance);
      setPanelState({ list: res.data || [], showSearch: false });
    } catch (e) {
      setToast({ message: e.message || "Error loading philosophies", tone: "error" });
      setPanelState({ list: [] });
    } finally {
      setSearching(false);
    }
  };

  const fetchDetail = async () => {
    if (!distance || !philosophyLookup) return;
    setSearching(true);
    try {
      const res = await philosophyApi.byId(distance, philosophyLookup);
      loadForm(res.data);
      setMode("edit");
      setToast({ message: "Loaded philosophy", tone: "success" });
      setPanelState({ showSearch: false });
    } catch (e) {
      setToast({ message: e.message || "Error fetching philosophy", tone: "error" });
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async () => {
    if (!form.distanceType || !form.name) {
      setToast({ message: "distanceType and name are required", tone: "error" });
      return;
    }
    setSaving(true);
    try {
      await philosophyApi.create({
        distanceType: form.distanceType,
        description: form.description,
        name: form.name,
        philosophies: parseListInput(pillarsInput),
        baseWorkoutPlanId: form.baseWorkoutPlanId,
        favWorkouts: parseListInput(favWorkoutsInput),
      });
      setToast({ message: "Philosophy created", tone: "success" });
      await fetchByDistance();
      loadForm({ distanceType: form.distanceType });
      setMode("search");
    } catch (e) {
      setToast({ message: e.message || "Error creating philosophy", tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.philosophyId || !form.distanceType) {
      setToast({ message: "distanceType + id needed", tone: "error" });
      return;
    }
    setSaving(true);
    try {
      await philosophyApi.update(form.distanceType, form.philosophyId, {
        distanceType: form.distanceType,
        description: form.description,
        name: form.name,
        philosophies: parseListInput(pillarsInput),
        baseWorkoutPlanId: form.baseWorkoutPlanId,
        favWorkouts: parseListInput(favWorkoutsInput),
      });
      setToast({ message: "Philosophy updated", tone: "success" });
      await fetchByDistance();
      setMode("search");
    } catch (e) {
      setToast({ message: e.message || "Error updating philosophy", tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (distanceType, id) => {
    if (!distanceType || !id) return;
    setSaving(true);
    try {
      await philosophyApi.delete(distanceType, id);
      setToast({ message: "Philosophy deleted", tone: "success" });
      loadForm(blank);
      await fetchByDistance();
      setMode("search");
    } catch (e) {
      setToast({ message: e.message || "Error deleting philosophy", tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderSearch = () => (
    <Card
      title="Philosophies"
      action={
        <div style={{ display: "flex", gap: 8 }}>
          <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>{showSearch ? "Hide search" : "Show search"}</Button>
          <Button onClick={() => { loadForm(blank); setMode("edit"); }}>+ Create</Button>
        </div>
      }
    >
      {showSearch && (
        <Card title="Browse by distance">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Select label="Distance" value={distance} onChange={setDistance} options={distanceOptions} />
            <Button onClick={fetchByDistance} loading={searching}>Load</Button>
          </div>
        </Card>
      )}
      {list.length > 0 && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          {list.map((p) => (
            <div
              key={p.philosophyId}
              style={{
                padding: 12,
                borderRadius: 12,
                border: "1px solid rgba(15,23,42,0.08)",
                background: "rgba(25,164,99,0.06)",
                cursor: "pointer",
              }}
              onClick={() => { loadForm(p); setMode("edit"); }}
            >
              <div style={{ fontWeight: 700 }}>{p.name}</div>
              <div style={{ fontSize: 13, color: "var(--stone-700)" }}>{p.description}</div>
              <div style={{ marginTop: 6 }}>
                <Button tone="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(p.distanceType || distance, p.philosophyId); }} disabled={saving}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  const renderLookup = () => (
    <Card title="Lookup specific">
      <Input label="Distance Type" value={distance} onChange={setDistance} />
      <Input label="Philosophy Id" value={philosophyLookup} onChange={setPhilosophyLookup} />
      <Button full onClick={fetchDetail}>
        Fetch
      </Button>
    </Card>
  );

  const renderEdit = () => (
    <Card
      title={form.philosophyId ? "Edit Philosophy" : "Create Philosophy"}
      action={<Button tone="ghost" onClick={() => setMode("search")}>← Back</Button>}
    >
      {form.philosophyId && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Philosophy Id</div>
          <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(15,23,42,0.03)", fontWeight: 700 }}>
            {form.philosophyId}
          </div>
        </div>
      )}
      {(form.philosophyVersion || form.lastUpdated) && (
        <div style={{ marginBottom: 12, color: "var(--stone-700)", fontSize: 13 }}>
          {form.philosophyVersion && <div>Version: {form.philosophyVersion}</div>}
          {form.lastUpdated && <div>Last Updated: {form.lastUpdated}</div>}
        </div>
      )}
      <Select
        label="Distance Type"
        value={form.distanceType}
        onChange={(v) => setForm({ ...form, distanceType: v })}
        options={distanceOptions}
      />
      <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
      <TextArea label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
      <TextArea
        label="Pillars (one per line)"
        placeholder="E.g. Build base, Strength work..."
        value={pillarsInput}
        onChange={(v) => {
          setPillarsInput(v);
          setForm({ ...form, philosophies: parseListInput(v) });
        }}
      />
      <Input
        label="Base Workout Plan Id"
        value={form.baseWorkoutPlanId}
        onChange={(v) => setForm({ ...form, baseWorkoutPlanId: v })}
      />
      <TextArea
        label="Favorite workouts (one ID per line)"
        placeholder="workout-123\nworkout-456"
        value={favWorkoutsInput}
        onChange={(v) => {
          setFavWorkoutsInput(v);
          setForm({ ...form, favWorkouts: parseListInput(v) });
        }}
      />
      <div style={{ display: "flex", gap: 10 }}>
        {!form.philosophyId && (
          <Button onClick={handleCreate} loading={saving}>Create</Button>
        )}
        {form.philosophyId && (
          <>
            <Button tone="secondary" onClick={handleUpdate} loading={saving}>Update</Button>
            <Button tone="ghost" onClick={() => handleDelete(form.distanceType, form.philosophyId)} disabled={saving}>
              Delete
            </Button>
          </>
        )}
      </div>
    </Card>
  );

  return (
    <>
      <TwoColumn
        left={mode === "search" ? renderSearch() : renderEdit()}
        right={mode === "search" && showSearch ? renderLookup() : <div />}
      />
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </>
  );
}
